import { State, StateInvoke } from '@people-science/state-machine';

import api, { createNamedError, FetchError } from '../../../api/api';
import { StudyParticipant } from '../../../models/StudyParticipant';
import { ServicePlugin } from '../ServicePlugin';

const RETRY_DELAYS_MS = [1000, 3000];

export interface QuitStudyParticipantInvokeData {
  studyParticipantId: number;
}

const isAbortError = (err: any): boolean => err?.name === 'AbortError';

const shouldRetry = (err: any): boolean => {
  if (!err) return false;
  if (isAbortError(err)) return false;
  if (err?.name === 'TimeoutError') return false;
  if (typeof err.statusCode === 'number') return err.statusCode >= 500;
  return true;
};

const sleep = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(createNamedError('AbortError', 'Aborted'));
      return;
    }
    const timeoutId = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timeoutId);
      reject(createNamedError('AbortError', 'Aborted'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });

/**
 * State-machine service for quitting (deleting) a study participant. Ported from
 * useDeleteStudyParticipant into a signal-driven service so the form state
 * machine owns the async work, cancellation, and onDone/onError routing
 * (CD-207). The destructive DELETE is now driven by the AbortSignal the machine
 * passes (state exit / unmount), so leaving the screen cancels the in-flight
 * request instead of letting it run to completion — fixing the prior path that
 * had no AbortController and kept deleting after navigation.
 */
const quitStudyParticipantService = async (
  _state: State<any> | undefined,
  invoke: StateInvoke[],
  { signal }: { signal: AbortSignal }
): Promise<StudyParticipant> => {
  const data = invoke[0]?.data as QuitStudyParticipantInvokeData | undefined;
  const studyParticipantId = data?.studyParticipantId;

  if (!studyParticipantId || studyParticipantId <= 0) {
    throw {
      statusCode: 400,
      error: 'BadRequest',
      message: 'Study participant is not available yet.'
    } as FetchError;
  }

  const path = `study-participants/${studyParticipantId}`;

  let lastError: any;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (signal.aborted) throw createNamedError('AbortError', 'Aborted');
    if (attempt > 0) {
      await sleep(RETRY_DELAYS_MS[attempt - 1], signal);
    }
    try {
      const { response, abort } = api.delete(path, {});
      const onAbort = () => abort();
      signal.addEventListener('abort', onAbort, { once: true });
      let result: Response;
      try {
        result = await response;
      } finally {
        signal.removeEventListener('abort', onAbort);
      }

      if (result.ok) {
        // A 204 / empty body is still success — never re-issue a destructive
        // DELETE because the response body was unparseable.
        let json: StudyParticipant;
        try {
          json = await result.json();
        } catch {
          json = {} as StudyParticipant;
        }
        if (signal.aborted) throw createNamedError('AbortError', 'Aborted');
        return json;
      }

      try {
        lastError = await result.json();
      } catch {
        lastError = {
          statusCode: result.status,
          message: result.statusText
        };
      }
      if (!shouldRetry(lastError)) break;
    } catch (err) {
      if (isAbortError(err)) throw err;
      lastError = err;
      if (!shouldRetry(err)) break;
    }
  }

  throw lastError;
};

export const QuitStudyParticipantServicePlugin: ServicePlugin = {
  type: 'quitStudyParticipant',
  service: quitStudyParticipantService
};
